import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/config';
import DashboardHeader from '../common/DashboardHeader';
import {
  TruckIcon,
  WrenchScrewdriverIcon,
  CreditCardIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  BellIcon,
  ShieldCheckIcon,
  PhoneIcon,
  MapPinIcon,
  PlusIcon,
  ArrowRightIcon,
  StarIcon,
  ExclamationCircleIcon,
  TicketIcon,
  ChartBarIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface Vehicle {
  id: string;
  make: string;
  model: string;
  year: number;
  plateNumber: string;
  color: string;
  vin: string;
  mileage: number;
  nextServiceDue: string;
  lastServiceDate: string;
  imageUrl?: string;
}

interface ServiceHistory {
  id: string;
  vehicleId: string;
  jobCardNumber: string;
  date: string;
  service: string;
  technician: string;
  cost: number;
  status: 'completed' | 'in_progress' | 'scheduled';
  invoice?: string;
  rating?: number;
  notes?: string;
}

interface Appointment {
  id: string;
  vehicleId: string;
  vehicle: string;
  service: string;
  date: string;
  time: string;
  status: 'confirmed' | 'pending' | 'cancelled';
  estimatedDuration: string;
  estimatedCost?: number;
  garageName: string;
  garagePhone: string;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  date: string;
  vehicle: string;
  amount: number;
  status: 'paid' | 'pending' | 'overdue';
  dueDate: string;
  paymentMethod?: string;
  items: Array<{
    description: string;
    quantity: number;
    price: number;
  }>;
}

interface DashboardStats {
  totalVehicles: number;
  totalServices: number;
  totalSpent: number;
  upcomingAppointments: number;
  pendingInvoices: number;
  loyaltyPoints: number;
  memberSince: string;
  preferredGarage: string;
}

const CustomerDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [recentServices, setRecentServices] = useState<ServiceHistory[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [pendingInvoices, setPendingInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch dashboard data from API
      const response = await apiClient.get('/customer/dashboard');
      const data = response.data;
      
      // Process stats
      setStats({
        totalVehicles: data.stats.totalVehicles || 0,
        totalServices: data.stats.totalServices || 0,
        totalSpent: data.stats.totalSpent || 0,
        upcomingAppointments: data.stats.upcomingAppointments || 0,
        pendingInvoices: data.stats.pendingInvoices || 0,
        loyaltyPoints: data.stats.loyaltyPoints || 0,
        memberSince: data.stats.memberSince || new Date().getFullYear().toString(),
        preferredGarage: data.stats.preferredGarage || 'Not set',
      });

      // Process vehicles
      const vehiclesData = data.vehicles || [];
      setVehicles(vehiclesData.map((v: any) => ({
        id: v.id.toString(),
        make: v.make,
        model: v.model,
        year: v.year,
        plateNumber: v.plateNumber,
        color: v.color,
        vin: v.vin,
        mileage: v.mileage,
        nextServiceDue: v.nextServiceDue || '',
        lastServiceDate: v.lastServiceDate || '',
        imageUrl: v.imageUrl,
      })));

      // Process recent services
      const servicesData = data.recentServices || [];
      setRecentServices(servicesData.map((s: any) => ({
        id: s.id,
        vehicleId: s.vehicleId,
        jobCardNumber: s.jobCardNumber,
        date: s.date,
        service: s.service,
        technician: s.technician,
        cost: s.cost,
        status: s.status,
        rating: s.rating,
        notes: s.notes,
      })));

      // Process appointments
      const appointmentsData = data.appointments || [];
      setAppointments(appointmentsData.map((a: any) => ({
        id: a.id,
        vehicleId: a.vehicleId,
        vehicle: a.vehicle,
        service: a.service,
        date: a.date,
        time: a.time,
        status: a.status,
        estimatedDuration: a.estimatedDuration,
        estimatedCost: a.estimatedCost,
        garageName: a.garageName,
        garagePhone: a.garagePhone,
      })));

      // Process pending invoices
      const invoicesData = data.pendingInvoices || [];
      setPendingInvoices(invoicesData.map((i: any) => ({
        id: i.id,
        invoiceNumber: i.invoiceNumber,
        date: i.date,
        vehicle: i.vehicle,
        amount: i.amount,
        status: i.status,
        dueDate: i.dueDate,
        paymentMethod: i.paymentMethod,
        items: i.items || [],
      })));

    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      // Show error message to user
      // You might want to add a toast notification here
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `${amount.toFixed(3)} KWD`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
      case 'paid':
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'overdue':
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const renderServiceChart = () => {
    const serviceTypes = ['Oil Change', 'Brake Service', 'Full Service', 'AC Service', 'Others'];
    const serviceCounts = [8, 4, 6, 3, 3];

    const data = {
      labels: serviceTypes,
      datasets: [
        {
          data: serviceCounts,
          backgroundColor: [
            '#008B8B',
            '#FF8C00',
            '#F59E0B',
            '#10B981',
            '#8B5CF6',
          ],
          borderWidth: 2,
          borderColor: '#ffffff',
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom' as const,
        },
        title: {
          display: true,
          text: 'Service History by Type',
        },
      },
    };

    return <Doughnut data={data} options={options} />;
  };

  const renderSpendingChart = () => {
    const data = {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      datasets: [
        {
          label: 'Monthly Spending',
          data: [185, 0, 245, 125, 0, 45],
          borderColor: '#008B8B',
          backgroundColor: 'rgba(0, 139, 139, 0.1)',
          fill: true,
          tension: 0.4,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        title: {
          display: true,
          text: 'Service Spending (Last 6 Months)',
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: function(value: any) {
              return value + ' KWD';
            },
          },
        },
      },
    };

    return <Line data={data} options={options} />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader 
        userName={user?.name}
        userRole="customer"
        showNotifications={true}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Welcome back, {user?.name || 'Customer'}
              </h1>
              <p className="text-gray-600 mt-1">
                Manage your vehicles and service history
              </p>
            </div>
            <div className="mt-4 sm:mt-0 flex items-center space-x-3">
              <div className="bg-[#008B8B] text-white px-4 py-2 rounded-lg">
                <div className="text-xs">Loyalty Points</div>
                <div className="text-lg font-bold">{stats?.loyaltyPoints || 0} pts</div>
              </div>
              <button 
                onClick={() => navigate('/dashboard/appointments')}
                className="bg-[#FF8C00] text-white px-4 py-2 rounded-lg hover:bg-[#E67C00]"
              >
                Book Service
              </button>
            </div>
          </div>
        </div>

        {/* Service Reminder Alert */}
        {vehicles.some(v => new Date(v.nextServiceDue) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)) && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <ExclamationCircleIcon className="w-5 h-5 text-yellow-600 mr-2" />
              <p className="text-sm text-yellow-800">
                <span className="font-semibold">Service Reminder:</span> Your Toyota Camry is due for service on Feb 15, 2024.
                <button 
                  onClick={() => navigate('/dashboard/appointments')}
                  className="ml-2 text-yellow-900 underline font-medium"
                >
                  Schedule Now
                </button>
              </p>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-[#008B8B]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">My Vehicles</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">{stats?.totalVehicles || 0}</p>
                <p className="text-sm text-gray-500 mt-1">Registered</p>
              </div>
              <div className="p-4 bg-[#008B8B]/10 rounded-xl">
                <TruckIcon className="w-8 h-8 text-[#008B8B]" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-[#FF8C00]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Services</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">{stats?.totalServices || 0}</p>
                <p className="text-sm text-gray-500 mt-1">Completed</p>
              </div>
              <div className="p-4 bg-[#FF8C00]/10 rounded-xl">
                <WrenchScrewdriverIcon className="w-8 h-8 text-[#FF8C00]" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-[#10B981]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Spent</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">
                  {formatCurrency(stats?.totalSpent || 0)}
                </p>
                <p className="text-sm text-gray-500 mt-1">Lifetime</p>
              </div>
              <div className="p-4 bg-[#10B981]/10 rounded-xl">
                <CreditCardIcon className="w-8 h-8 text-[#10B981]" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-[#F59E0B]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Invoices</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">{stats?.pendingInvoices || 0}</p>
                <p className="text-sm text-orange-600 mt-1">
                  {formatCurrency(pendingInvoices.reduce((sum, inv) => sum + inv.amount, 0))} due
                </p>
              </div>
              <div className="p-4 bg-[#F59E0B]/10 rounded-xl">
                <DocumentTextIcon className="w-8 h-8 text-[#F59E0B]" />
              </div>
            </div>
          </div>
        </div>

        {/* My Vehicles Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <TruckIcon className="w-5 h-5 text-[#008B8B] mr-2" />
              My Vehicles
            </h2>
            <button 
              onClick={() => setShowAddVehicleModal(true)}
              className="text-sm bg-[#008B8B] text-white px-4 py-2 rounded-lg hover:bg-[#006666] flex items-center"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Add Vehicle
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {vehicles.map((vehicle) => (
              <div key={vehicle.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="h-48 bg-gray-200 relative">
                  {vehicle.imageUrl && (
                    <img 
                      src={vehicle.imageUrl} 
                      alt={`${vehicle.make} ${vehicle.model}`}
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full shadow">
                    <span className="text-sm font-medium">{vehicle.plateNumber}</span>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {vehicle.year} {vehicle.make} {vehicle.model}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">{vehicle.color} • {vehicle.mileage.toLocaleString()} km</p>
                  
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Next Service</span>
                      <span className="font-medium text-[#008B8B]">
                        {new Date(vehicle.nextServiceDue).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Last Service</span>
                      <span className="text-gray-900">
                        {new Date(vehicle.lastServiceDate).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-6 flex space-x-3">
                    <button 
                      onClick={() => navigate(`/dashboard/vehicles/${vehicle.id}`)}
                      className="flex-1 bg-[#008B8B] text-white px-4 py-2 rounded-lg hover:bg-[#006666] text-sm"
                    >
                      View Details
                    </button>
                    <button 
                      onClick={() => navigate('/dashboard/appointments')}
                      className="flex-1 border border-[#008B8B] text-[#008B8B] px-4 py-2 rounded-lg hover:bg-gray-50 text-sm"
                    >
                      Book Service
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="h-64">
              {renderServiceChart()}
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="h-64">
              {renderSpendingChart()}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button 
              onClick={() => navigate('/dashboard/appointments')}
              className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all text-left border-l-4 border-[#008B8B] hover:scale-105"
            >
              <CalendarIcon className="w-8 h-8 text-[#008B8B] mb-3" />
              <h3 className="font-semibold text-gray-900">Book Appointment</h3>
              <p className="text-sm text-gray-600 mt-1">Schedule your next service</p>
            </button>

            <button 
              onClick={() => navigate('/dashboard/invoices')}
              className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all text-left border-l-4 border-[#FF8C00] hover:scale-105"
            >
              <CreditCardIcon className="w-8 h-8 text-[#FF8C00] mb-3" />
              <h3 className="font-semibold text-gray-900">Pay Invoice</h3>
              <p className="text-sm text-gray-600 mt-1">View and pay pending bills</p>
            </button>

            <button 
              onClick={() => navigate('/dashboard/service-history')}
              className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all text-left border-l-4 border-[#10B981] hover:scale-105"
            >
              <DocumentTextIcon className="w-8 h-8 text-[#10B981] mb-3" />
              <h3 className="font-semibold text-gray-900">Service History</h3>
              <p className="text-sm text-gray-600 mt-1">View past services</p>
            </button>

            <button 
              onClick={() => navigate('/dashboard/offers')}
              className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all text-left border-l-4 border-[#8B5CF6] hover:scale-105"
            >
              <TicketIcon className="w-8 h-8 text-[#8B5CF6] mb-3" />
              <h3 className="font-semibold text-gray-900">Special Offers</h3>
              <p className="text-sm text-gray-600 mt-1">View exclusive deals</p>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upcoming Appointments */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Upcoming Appointments</h2>
              <button 
                onClick={() => navigate('/dashboard/appointments')}
                className="text-sm text-[#008B8B] hover:text-[#006666] font-medium"
              >
                View All
              </button>
            </div>
            
            {appointments.length > 0 ? (
              <div className="space-y-4">
                {appointments.map((appointment) => (
                  <div key={appointment.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(appointment.status)}`}>
                        {appointment.status.toUpperCase()}
                      </span>
                      <span className="text-sm text-gray-500">
                        {new Date(appointment.date).toLocaleDateString()}
                      </span>
                    </div>
                    
                    <h4 className="font-medium text-gray-900">{appointment.service}</h4>
                    <p className="text-sm text-gray-600 mt-1">{appointment.vehicle}</p>
                    
                    <div className="mt-3 space-y-1">
                      <div className="flex items-center text-sm text-gray-500">
                        <ClockIcon className="w-4 h-4 mr-2" />
                        <span>{appointment.time} • {appointment.estimatedDuration}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <MapPinIcon className="w-4 h-4 mr-2" />
                        <span>{appointment.garageName}</span>
                      </div>
                      {appointment.estimatedCost && (
                        <div className="flex items-center text-sm text-gray-500">
                          <CreditCardIcon className="w-4 h-4 mr-2" />
                          <span>Est. {formatCurrency(appointment.estimatedCost)}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-4 flex space-x-3">
                      <button className="flex-1 text-sm bg-[#008B8B] text-white px-3 py-1.5 rounded hover:bg-[#006666]">
                        View Details
                      </button>
                      <button className="flex-1 text-sm border border-gray-300 text-gray-700 px-3 py-1.5 rounded hover:bg-gray-50">
                        Reschedule
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No upcoming appointments</p>
                <button 
                  onClick={() => navigate('/dashboard/appointments')}
                  className="mt-3 text-sm text-[#008B8B] hover:text-[#006666] font-medium"
                >
                  Book an Appointment
                </button>
              </div>
            )}
          </div>

          {/* Recent Services */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Recent Services</h2>
              <button 
                onClick={() => navigate('/dashboard/service-history')}
                className="text-sm text-[#008B8B] hover:text-[#006666] font-medium"
              >
                View All
              </button>
            </div>
            
            <div className="space-y-4">
              {recentServices.slice(0, 3).map((service) => (
                <div key={service.id} className="border-b last:border-0 pb-4 last:pb-0">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900">
                      #{service.jobCardNumber}
                    </span>
                    <span className="text-sm text-gray-500">
                      {new Date(service.date).toLocaleDateString()}
                    </span>
                  </div>
                  
                  <p className="text-sm font-medium text-gray-900">{service.service}</p>
                  <p className="text-sm text-gray-600">
                    {vehicles.find(v => v.id === service.vehicleId)?.make} {vehicles.find(v => v.id === service.vehicleId)?.model}
                  </p>
                  
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center">
                      {service.rating && (
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <StarIcon 
                              key={i} 
                              className={`w-4 h-4 ${i < (service.rating || 0) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    <span className="text-sm font-medium text-[#008B8B]">
                      {formatCurrency(service.cost)}
                    </span>
                  </div>
                  
                  {service.notes && (
                    <p className="text-xs text-gray-500 mt-2 italic">"{service.notes}"</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Pending Invoices */}
        {pendingInvoices.length > 0 && (
          <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <ExclamationCircleIcon className="w-5 h-5 text-orange-500 mr-2" />
                Pending Invoices
              </h2>
              <button 
                onClick={() => navigate('/dashboard/invoices')}
                className="text-sm text-[#008B8B] hover:text-[#006666] font-medium"
              >
                View All
              </button>
            </div>
            
            <div className="space-y-4">
              {pendingInvoices.map((invoice) => (
                <div key={invoice.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-sm font-medium text-gray-900">
                        {invoice.invoiceNumber}
                      </span>
                      <span className={`ml-2 px-2 py-1 text-xs font-medium rounded ${getStatusColor(invoice.status)}`}>
                        {invoice.status.toUpperCase()}
                      </span>
                    </div>
                    <span className="text-lg font-bold text-gray-900">
                      {formatCurrency(invoice.amount)}
                    </span>
                  </div>
                  
                  <div className="space-y-1 mb-3">
                    <p className="text-sm text-gray-600">{invoice.vehicle}</p>
                    <p className="text-sm text-gray-500">
                      Due: {new Date(invoice.dueDate).toLocaleDateString()}
                    </p>
                  </div>
                  
                  <button 
                    onClick={() => navigate(`/dashboard/invoices/${invoice.id}`)}
                    className="w-full bg-[#008B8B] text-white px-4 py-2 rounded-lg hover:bg-[#006666] flex items-center justify-center"
                  >
                    <CreditCardIcon className="w-4 h-4 mr-2" />
                    Pay Now
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Loyalty & Benefits */}
        <div className="mt-8 bg-gradient-to-r from-[#008B8B] to-[#006666] rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center">
              <StarIcon className="w-5 h-5 mr-2" />
              Loyalty Program
            </h2>
            <span className="text-2xl font-bold">{stats?.loyaltyPoints || 0} Points</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/10 rounded-lg p-4">
              <h3 className="font-medium mb-2">Free Oil Change</h3>
              <p className="text-sm opacity-90">Redeem at 500 points</p>
              <div className="mt-2 bg-white/20 rounded-full h-2">
                <div 
                  className="bg-white h-2 rounded-full" 
                  style={{ width: `${Math.min((stats?.loyaltyPoints || 0) / 500 * 100, 100)}%` }}
                ></div>
              </div>
            </div>
            
            <div className="bg-white/10 rounded-lg p-4">
              <h3 className="font-medium mb-2">20% Service Discount</h3>
              <p className="text-sm opacity-90">Redeem at 300 points</p>
              <div className="mt-2 bg-white/20 rounded-full h-2">
                <div 
                  className="bg-white h-2 rounded-full" 
                  style={{ width: `${Math.min((stats?.loyaltyPoints || 0) / 300 * 100, 100)}%` }}
                ></div>
              </div>
            </div>
            
            <div className="bg-white/10 rounded-lg p-4">
              <h3 className="font-medium mb-2">Premium Member</h3>
              <p className="text-sm opacity-90">Since {stats?.memberSince}</p>
              <p className="text-xs mt-2 opacity-75">Earn 2x points on all services</p>
            </div>
          </div>
        </div>

        {/* Garage Info */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <InformationCircleIcon className="w-5 h-5 text-[#008B8B] mr-2" />
            Your Preferred Garage
          </h2>
          
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-medium text-gray-900">{stats?.preferredGarage}</h3>
              <p className="text-sm text-gray-600 mt-1">Certified Service Center</p>
              
              <div className="mt-3 space-y-2">
                <div className="flex items-center text-sm text-gray-600">
                  <MapPinIcon className="w-4 h-4 mr-2 text-gray-400" />
                  <span>Al-Salam Street, Block 3, Kuwait City</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <PhoneIcon className="w-4 h-4 mr-2 text-gray-400" />
                  <span>+965 2222 3333</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <ClockIcon className="w-4 h-4 mr-2 text-gray-400" />
                  <span>Open: Sat-Thu 8:00 AM - 6:00 PM</span>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col items-end">
              <div className="flex items-center mb-2">
                {[...Array(5)].map((_, i) => (
                  <StarIcon key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-sm text-gray-600">4.8/5 (234 reviews)</p>
              <button 
                onClick={() => navigate('/dashboard/garages')}
                className="mt-3 text-sm text-[#008B8B] hover:text-[#006666] font-medium"
              >
                Change Garage
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CustomerDashboard;